// What this device shows of notifications outside the app: the pushes in its
// tray and the count on its icon (M2's P0 Also, NOTIFICATIONS.md §2).
//
// READ ON ONE DEVICE IS READ ON THE OTHERS, and inside the app that was
// already true (B-189). Outside it was not: a push read on the desktop stayed
// in the phone's tray, and the count a push put on the installed icon stayed
// there after everything was read. So when the app learns the unread count
// — on opening, on coming back on screen, after a read, and while on screen
// whenever the count moves — it sets the icon's count to it and closes this
// device's shown pushes that are read. B-241.
//
// THE LIMIT IS THE PLATFORM'S: a device whose app is not running cannot be
// told. The only way to reach it is a push, and a push that shows nothing is
// one browsers refuse (Chrome shows its own notice in its place; Safari drops
// the subscription after a few). So the tray is tidied the next time the app
// runs on that device, not the moment something is read elsewhere.

import { NotificationPage } from '@fsp/domain';
import { get } from './api';
import { WORKER_URL } from './serviceWorker';

type Badging = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

/** The installed icon's count. A browser tab, or a browser without badges, ignores it. */
async function setBadge(unread: number): Promise<void> {
  const nav = navigator as Badging;
  try {
    if (unread > 0) await nav.setAppBadge?.(unread);
    else await nav.clearAppBadge?.();
  } catch {
    // Not installed, or badges are off for this app: nothing to show it on.
  }
}

/**
 * Set the icon's count to `unread` and close the shown pushes that are read.
 * With nothing unread, every shown push goes; otherwise the first page of the
 * record says which are read, and only those go — a push older than the page
 * is left rather than guessed at.
 */
export async function settleTray(unread: number): Promise<void> {
  await setBadge(unread);
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(WORKER_URL);
    if (reg === undefined) return;
    const shown = await reg.getNotifications();
    if (shown.length === 0) return;
    if (unread === 0) {
      for (const n of shown) n.close();
      return;
    }
    const page = await get('/me/notifications', NotificationPage);
    if (page.status !== 'ok') return;
    // A FOLDED WEEK (B-439) is one row for several notices, each of which may
    // have pushed: read, it closes every one of them.
    const read = new Set(
      page.data.items
        .filter((n) => n.readAt !== null)
        .flatMap((n) => (n.groupIds.length > 0 ? n.groupIds : [n.id])),
    );
    for (const n of shown) {
      const id = (n.data as { id?: unknown } | null)?.id;
      if (typeof id === 'string' && read.has(id)) n.close();
    }
  } catch {
    // A tray that cannot be read is left as it is; the next count tries again.
  }
}
